"use client";

import { createContext, useContext, useEffect, useRef, useState } from "react";

import { hasMotion } from "@/app/lib/motion/MotionScript";

/** A monitor starts once it is a quarter of the viewport clear of the bottom
    edge, so the reader is looking at it when it does. */
const START_ROOT_MARGIN = "0px 0px -25% 0px";

interface DeskMonitorState {
  /** Latched the first time the monitor is playing; its timelines count from then. */
  isStarted: boolean;
  /** On screen, in a visible tab, with motion on. The leaves that keep time tick only while this holds. */
  isPlaying: boolean;
}

const DeskMonitorContext = createContext<DeskMonitorState>({
  isStarted: false,
  isPlaying: false,
});

/** The monitor a leaf sits on. Outside one, a leaf never plays. */
export function useDeskMonitor(): DeskMonitorState {
  return useContext(DeskMonitorContext);
}

/**
 * One monitor of the problem desktop, the client wrapper around windows that
 * render on the server. It sets `data-started` once the monitor is on screen,
 * and the windows' CSS plays every timeline from that moment with delays
 * computed at render; the few leaves that keep time (the call's clock, the
 * counts) read this monitor's state. On a phone the monitors stack, so each
 * starts on its own.
 *
 * Nothing starts without `data-motion` (reduced motion, or no script), so the
 * windows keep the final state they render with.
 */
export default function DeskMonitor({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<DeskMonitorState>({ isStarted: false, isPlaying: false });

  useEffect(() => {
    const node = ref.current;

    if (!node || !hasMotion()) {
      return;
    }

    let isIntersecting = false;

    // A background tab still reports the monitor as in view, so visibility is
    // checked too: nothing starts, or keeps time, where nobody can see it.
    function update() {
      const isPlaying = isIntersecting && document.visibilityState === "visible";

      setState((previous) =>
        previous.isPlaying === isPlaying
          ? previous
          : { isStarted: previous.isStarted || isPlaying, isPlaying },
      );
    }

    const observer = new IntersectionObserver(
      (entries) => {
        isIntersecting = entries[entries.length - 1].isIntersecting;
        update();
      },
      { rootMargin: START_ROOT_MARGIN },
    );

    observer.observe(node);
    document.addEventListener("visibilitychange", update);

    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", update);
    };
  }, []);

  return (
    <DeskMonitorContext.Provider value={state}>
      <div className={className} ref={ref} data-started={state.isStarted || undefined}>
        {children}
      </div>
    </DeskMonitorContext.Provider>
  );
}
